import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { fetchHomeSection, HomeSection } from "@/services/homeService";

export default function EarnWithHareeng() {
  const navigate = useNavigate();
  const [section, setSection] = useState<HomeSection<any> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHomeSection("features").then((data) => {
      setSection(data);
      setLoading(false);
    });
  }, []);

  // Modern shimmer for loading
  function ModernShimmer({
    className = "",
    height,
    width,
  }: {
    className?: string;
    height: string;
    width: string;
  }) {
    return (
      <div
        className={`relative overflow-hidden rounded-xl shadow-sm ${className}`}
        style={{ height, width }}
      >
        <div
          className="animate-shimmer absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-base-200 dark:via-base-100 dark:to-base-200"
          style={{ backgroundSize: "200% 100%" }}
        />
      </div>
    );
  }

  if (
    typeof document !== "undefined" &&
    !document.getElementById("modern-shimmer-style")
  ) {
    const style = document.createElement("style");
    style.id = "modern-shimmer-style";
    style.innerHTML = `
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .animate-shimmer {
        animation: shimmer 1.5s linear infinite;
      }
    `;
    document.head.appendChild(style);
  }

  return (
    <section className="bg-base-100 mx-auto flex w-full max-w-7xl flex-col-reverse items-center gap-10 px-4 py-16 md:flex-row">
      {/* Text Content */}
      <div className="flex-1 z-10">
        {loading ? (
          <>
            <ModernShimmer
              className="mx-auto mb-4"
              height="3.5rem"
              width="80%"
            />
            <ModernShimmer className="mx-auto mb-6" height="2rem" width="90%" />
            <ModernShimmer
              className="mx-auto mb-2"
              height="1.5rem"
              width="70%"
            />
            <ModernShimmer
              className="mx-auto mb-2"
              height="1.5rem"
              width="60%"
            />
            <ModernShimmer
              className="mx-auto mb-6"
              height="1.5rem"
              width="50%"
            />
            <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
              <ModernShimmer className="mb-2" height="3rem" width="180px" />
              <ModernShimmer className="mb-2" height="3rem" width="180px" />
            </div>
          </>
        ) : section && section.content ? (
          <>
            <h2 className="mb-4 text-3xl font-extrabold text-primary md:text-5xl">
              {section.content.title}
            </h2>
            <p className="mb-6 text-lg text-base-content/70">
              {section.content.subtitle}
            </p>
            <ul className="mb-6 list-disc space-y-2 pl-5 text-base-content/80">
              {section.content.bullets?.map((b: string, i: number) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
            <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
              {section.content.buttons?.map((btn: any, i: number) => (
                <button
                  className={
                    i === 0
                      ? "btn btn-neutral btn-lg w-full font-semibold sm:w-auto"
                      : "btn btn-primary btn-lg w-full font-semibold sm:w-auto"
                  }
                  key={btn.label + i}
                  onClick={() => navigate(btn.link)}
                  type="button"
                >
                  {btn.label}
                </button>
              ))}
            </div>
          </>
        ) : null}
      </div>
      {/* Image */}
      <div className="flex flex-1 justify-center">
        {loading ? (
          <ModernShimmer className="max-w-xl" height="350px" width="100%" />
        ) : (
          <img
            alt={section?.content?.title || "Person driving a car"}
            className="w-full max-w-xl rounded-2xl object-cover shadow-lg"
            loading="lazy"
            src={
              section?.content?.image
                ? section.content.image.startsWith("http")
                  ? section.content.image
                  : `${import.meta.env.VITE_API_STORAGE_URL}${section.content.image}`
                : undefined
            }
          />
        )}
      </div>
    </section>
  );
}
